import { FC } from 'react';
import { Badge } from '@components/atoms/Badge';
import { Card } from '@components/atoms/Card';
import { useTranslation } from '@hooks/useTranslation';
import { DailySiteReportSections } from '@models/dailySiteReport.model';
import { groupReportByProject } from '@utils/dailySiteReport/groupReportByProject';
import { ReportSection } from './ReportSection';
import { ProjectReportCard } from './ProjectReportCard';
import { IncidentRow } from './IncidentRow';
import { CrewHoursTable } from './CrewHoursTable';
import { taskStatusLabel, txTypeLabel } from './labels';

interface ReportViewProps {
  sections: DailySiteReportSections;
}

/**
 * Sprint 33, story-170; redesigned sprint 38, story-206. Renders the stored `sections` JSON as one
 * card per project (regrouped client-side by `groupReportByProject`; the payload is unchanged),
 * with the email's section order inside each card, "Title (n)" headers, badges for status/type,
 * a compact crew-hours table, and empty sections collapsed. A trailing Stock card and a
 * report-level "tomorrow" block follow the cards. Tested against the shared fixture
 * `daily-site-report.v1.json`. Never renders a cost field — the payload itself never has one (D4).
 */

const STATUS_VARIANT: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error'> = {
  active: 'primary',
  inactive: 'secondary',
  complete: 'success',
  rejected: 'error',
  canceled: 'error',
  delegationCanceled: 'error',
  internalApproval: 'warning',
  clientApproval: 'warning',
};

const formatTime = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

/** Same heading as the email's section 5: "Looking ahead — tomorrow, Sat, Sep 26". */
const lookingAheadTitle = (template: string, tomorrowISO: string): string =>
  template.replace(
    '{date}',
    new Date(`${tomorrowISO}T12:00:00`).toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }),
  );

export const ReportView: FC<ReportViewProps> = ({ sections: s }) => {
  const t = useTranslation('dailySiteReport');
  const grouped = groupReportByProject(s);

  const statusBadge = (status: string) => (
    <Badge size="sm" variant={STATUS_VARIANT[status] ?? 'default'}>
      {taskStatusLabel(t, status)}
    </Badge>
  );

  const cardTitle = (name: string | null) => name || t.cardGeneral;
  const incidentsBadge = (n: number) =>
    n === 0 ? undefined : n === 1 ? t.cardIncidentsBadgeOne : t.cardIncidentsBadgeOther.replace('{n}', String(n));

  return (
    <div data-testid="daily-site-report-view">
      {grouped.isEmpty && (
        <Card className="p-4 mb-4">
          <p className="text-sm text-gray-500">{t.nothingToReport}</p>
        </Card>
      )}

      {grouped.cards.map((card) => (
        <ProjectReportCard key={card.key} title={cardTitle(card.projectName)} badge={incidentsBadge(card.incidents.length)}>
          {/* 1. Status changes & completions */}
          <ReportSection title={t.section1Title} count={card.statusChanges.length} truncated={grouped.truncated.statusChanges}>
            {card.statusChanges.map((item, i) => (
              <div key={`${item.taskId}-${i}`}>
                <p className="font-medium">{item.taskName}</p>
                <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                  {item.newStatus === 'complete' ? (
                    statusBadge('complete')
                  ) : (
                    <>
                      {item.previousStatus ? statusBadge(item.previousStatus) : <span>—</span>}
                      <span aria-hidden="true">→</span>
                      {statusBadge(item.newStatus)}
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {formatTime(item.at)} · {item.userName}
                </p>
              </div>
            ))}
          </ReportSection>

          {/* 2. Incidents */}
          <ReportSection title={t.section2Title} count={card.incidents.length} truncated={grouped.truncated.incidents}>
            {card.incidents.map((item) => (
              <IncidentRow key={item.incidentId} item={item} />
            ))}
          </ReportSection>

          {/* 3. Crew hours — absent (not shown empty) when null, per D5 */}
          <ReportSection title={t.section3Title} count={card.crewHours.length}>
            <CrewHoursTable entries={card.crewHours} />
          </ReportSection>

          {/* 4. Notes & documents */}
          <ReportSection title={t.section4Notes} count={card.notes.length} truncated={grouped.truncated.notes}>
            {card.notes.map((n) => (
              <p key={n.noteId}>
                {t.noteExcerptTemplate.replace('{excerpt}', n.excerpt)}
                <span className="text-xs text-gray-500">
                  {' '}
                  — {[n.taskName, n.userName, formatTime(n.at)].filter(Boolean).join(' · ')}
                </span>
              </p>
            ))}
          </ReportSection>
          <ReportSection title={t.section4Documents} count={card.documents.length} truncated={grouped.truncated.documents}>
            {card.documents.map((d) => (
              <p key={d.documentId}>
                📎 {d.filename} <span className="text-xs text-gray-500">— {formatTime(d.at)}</span>
              </p>
            ))}
          </ReportSection>
        </ProjectReportCard>
      ))}

      {/* Stock is warehouse-scoped, not project-scoped: its own trailing card. */}
      {grouped.stock.length > 0 && (
        <ProjectReportCard title={t.section4Stock}>
          {grouped.stock.map((w) => (
            <ReportSection key={w.warehouseId} title={w.warehouseName} count={w.entries.length}>
              {w.entries.map((e) => (
                <p key={e.transactionId} className="flex items-center gap-2">
                  <Badge size="sm" variant="secondary">
                    {txTypeLabel(t, e.type)}
                  </Badge>
                  <span>
                    {e.materialName} — {e.quantity} {e.unit ?? ''}
                  </span>
                </p>
              ))}
            </ReportSection>
          ))}
        </ProjectReportCard>
      )}

      {/* 5. Looking ahead — report-level block, grouped by project */}
      {grouped.lookingAhead && (
        <ProjectReportCard title={lookingAheadTitle(t.section5TitleWithDate, grouped.lookingAhead.tomorrowISO)}>
          {grouped.lookingAhead.groups.map((g) => (
            <div key={g.key} className="mb-3 last:mb-0">
              <p className="font-medium mb-1">{cardTitle(g.projectName)}</p>
              {g.tasksStarting.map((task) => (
                <p key={`start-${task.taskId}`}>
                  {t.lookingAheadStarting.replace('{task}', task.taskName).replace('{project}', cardTitle(g.projectName))}
                </p>
              ))}
              {g.tasksDue.map((task) => (
                <p key={`due-${task.taskId}`}>
                  {t.lookingAheadDue.replace('{task}', task.taskName).replace('{project}', cardTitle(g.projectName))}
                </p>
              ))}
              <ReportSection title={t.pendingIncidentsTitle} count={g.pendingIncidents.length}>
                {g.pendingIncidents.map((item) => (
                  <IncidentRow key={item.incidentId} item={item} linked />
                ))}
              </ReportSection>
            </div>
          ))}
        </ProjectReportCard>
      )}
    </div>
  );
};

export default ReportView;
